import React from "react";
import {
  FiAlertTriangle,
  FiArrowDown,
  FiArrowUp,
  FiClock,
  FiDollarSign,
  FiMinus,
  FiRefreshCw,
} from "react-icons/fi";
import { BOOKING_ISSUE_OPTIONS } from "../adminConstants";
import { formatCurrency, formatTimestamp, getIssueLabel } from "../adminUtils";

const AdminBookingsTab = ({
  t,
  isActive,
  bookingsRef,
  searchQuery,
  setSearchQuery,
  handleSearch,
  statusFilter,
  setStatusFilter,
  setBookingsPage,
  bookingStatusOptions,
  locationFilter,
  setLocationFilter,
  bookingLocationOptions,
  sortTimeDir,
  setSortTimeDir,
  toggleSortDirRequired,
  sortPriceDir,
  setSortPriceDir,
  toggleSortDir,
  issuesOnly,
  setIssuesOnly,
  handleResetFilters,
  bookingsLoading,
  bookingsError,
  bookings,
  bookingDetailsById,
  bookingDetailsLoadingId,
  expandedBookingId,
  handleBookingDetailsToggle,
  openRefundDrawer,
  isRefunding,
  refundBookingId,
  bookingStatusChoices,
  handleStatusOverride,
  statusUpdatingId,
  issueUpdatingId,
  handleIssueUpdate,
  handleManualCorrection,
  correctionBookingId,
  handleCorrectionSubmit,
  correctionNote,
  setCorrectionNote,
  isSavingNote,
  setCorrectionBookingId,
  handleResendBookingToken,
  handleReissueBookingToken,
  handleRevokeBookingToken,
  bookingTokenActionId,
  bookingsTotal,
  bookingsPage,
  bookingsPageSize,
  totalBookingsPages,
}) => (
  <div className={`admin-tab ${isActive ? "admin-tab--active" : ""}`.trim()}>
    <section
      ref={bookingsRef}
      className="admin-section admin-animate admin-animate--delay-5"
    >
      <header>
        <div>
          <p className="admin-section__eyebrow">
            {t("admin.bookings.eyebrow", "Bookings admin view")}
          </p>
          <h2>{t("admin.bookings.heading", "Bookings control")}</h2>
        </div>
      </header>
      <div className="admin-search">
        <input
          type="search"
          className="admin-input"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSearch();
            }
          }}
          placeholder={t(
            "admin.bookings.searchPlaceholder",
            "Search by booking ID, email, or phone"
          )}
          aria-label={t("admin.bookings.searchLabel", "Search bookings")}
        />
        <button
          type="button"
          className="admin-button admin-button--primary"
          onClick={handleSearch}
        >
          {t("admin.actions.search", "Search")}
        </button>
      </div>
      <div className="admin-filters">
        <div className="admin-filters__group">
          <label className="admin-form__label" htmlFor="statusFilter">
            {t("admin.bookings.statusFilter", "Status")}
          </label>
          <select
            id="statusFilter"
            className="admin-select admin-select--compact"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setBookingsPage(1);
            }}
          >
            {bookingStatusOptions.map((status) => (
              <option key={status} value={status}>
                {status === "all"
                  ? t("admin.bookings.allStatuses", "All statuses")
                  : status}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-filters__group">
          <label className="admin-form__label" htmlFor="locationFilter">
            {t("admin.bookings.locationFilter", "Location")}
          </label>
          <select
            id="locationFilter"
            className="admin-select admin-select--compact"
            value={locationFilter}
            onChange={(event) => {
              setLocationFilter(event.target.value);
              setBookingsPage(1);
            }}
          >
            {bookingLocationOptions.map((location) => (
              <option key={location.value} value={location.value}>
                {location.label}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-filters__group admin-filters__group--compact">
          <button
            type="button"
            id="sortTime"
            className={`admin-icon-toggle admin-icon-toggle--time ${
              sortTimeDir !== "none" ? "admin-icon-toggle--active" : ""
            }`.trim()}
            onClick={() => {
              setSortTimeDir((current) => toggleSortDirRequired(current));
              setBookingsPage(1);
            }}
            aria-label={t("admin.bookings.sortTime", "Sort by time")}
            title={t("admin.bookings.sortTime", "Sort by time")}
          >
            <FiClock aria-hidden="true" />
            {sortTimeDir === "asc" ? (
              <FiArrowUp aria-hidden="true" />
            ) : (
              <FiArrowDown aria-hidden="true" />
            )}
          </button>
        </div>
        <div className="admin-filters__group admin-filters__group--compact">
          <button
            type="button"
            id="sortPrice"
            className={`admin-icon-toggle admin-icon-toggle--price ${
              sortPriceDir !== "none" ? "admin-icon-toggle--active" : ""
            }`.trim()}
            onClick={() => {
              setSortPriceDir((current) => toggleSortDir(current));
              setBookingsPage(1);
            }}
            aria-label={t("admin.bookings.sortPrice", "Sort by price")}
            title={t("admin.bookings.sortPrice", "Sort by price")}
          >
            <FiDollarSign aria-hidden="true" />
            {sortPriceDir === "asc" ? (
              <FiArrowUp aria-hidden="true" />
            ) : sortPriceDir === "desc" ? (
              <FiArrowDown aria-hidden="true" />
            ) : (
              <FiMinus aria-hidden="true" />
            )}
          </button>
        </div>
        <div className="admin-filters__actions">
          <button
            type="button"
            className={`admin-icon-button admin-icon-button--issues ${
              issuesOnly ? "admin-icon-button--active" : ""
            }`.trim()}
            onClick={() => {
              setIssuesOnly((current) => !current);
              setBookingsPage(1);
            }}
            aria-label={t("admin.bookings.filterIssues", "Filter issues")}
            title={t("admin.bookings.filterIssues", "Filter issues")}
          >
            <FiAlertTriangle aria-hidden="true" focusable="false" />
          </button>
          <button
            type="button"
            className="admin-icon-button"
            onClick={handleResetFilters}
            aria-label={t("admin.bookings.resetFilters", "Reset filters")}
            title={t("admin.bookings.resetFilters", "Reset filters")}
          >
            <FiRefreshCw aria-hidden="true" focusable="false" />
          </button>
        </div>
      </div>
      <div className="admin-bookings">
        {bookingsLoading ? (
          <p className="admin-empty">
            {t("admin.bookings.loading", "Loading bookings...")}
          </p>
        ) : bookingsError ? (
          <p className="admin-empty">{bookingsError}</p>
        ) : bookings.length === 0 ? (
          <p className="admin-empty">
            {t("admin.bookings.empty", "No bookings match the current filters.")}
          </p>
        ) : (
          bookings.map((booking) => {
            const cachedDetail = bookingDetailsById[booking.id];
            const bookingDetail = cachedDetail && cachedDetail.id ? cachedDetail : booking;
            const isDetailLoading = bookingDetailsLoadingId === booking.id;
            const bookingToken = bookingDetail.bookingToken;
            const bookingTokenStatus = isDetailLoading
              ? "Loading"
              : bookingToken?.status || "Missing";
            const bookingTokenActionUrl = bookingToken?.actionUrl || "";
            const bookingTokenExpires = bookingToken?.expiresUtc;

            return (
              <article
                key={booking.id}
                className={`admin-booking ${
                  expandedBookingId === booking.id ? "admin-booking--active" : ""
                }`.trim()}
              >
                <div className="admin-booking__main">
                  <div>
                    <h3>{booking.id}</h3>
                    <p className="admin-booking__sub">
                      {booking.contactEmail || booking.guest}
                    </p>
                    <p className="admin-booking__meta">
                      {booking.item} · {booking.time}
                    </p>
                  </div>
                  <div className="admin-booking__badges">
                    <span className={`admin-pill admin-pill--${booking.statusTone}`}>
                      {booking.status}
                    </span>
                    {booking.lateReturn && (
                      <span className="admin-pill admin-pill--warning">
                        {t("admin.bookings.late", "Late")}
                      </span>
                    )}
                    {booking.issueTag && booking.issueTag !== "none" && (
                      <span className="admin-pill admin-pill--attention">
                        {getIssueLabel(booking.issueTag)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="admin-booking__footer">
                  <div className="admin-inline-actions">
                    <button
                      type="button"
                      className="admin-button admin-button--ghost"
                      onClick={() => handleBookingDetailsToggle(booking.id)}
                    >
                      {t("admin.actions.viewDetails", "Details")}
                    </button>
                    <button
                      type="button"
                      className="admin-button admin-button--danger"
                      onClick={() => openRefundDrawer(booking)}
                      disabled={isRefunding && refundBookingId === booking.id}
                    >
                      {t("admin.actions.refund", "Refund")}
                    </button>
                    <select
                      className="admin-select admin-select--compact"
                      value={booking.status}
                      onChange={(event) => {
                        const nextStatus = event.target.value;
                        if (nextStatus === booking.status) return;
                        handleStatusOverride(booking, nextStatus);
                      }}
                      disabled={statusUpdatingId === booking.id}
                    >
                      {bookingStatusChoices.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                    <select
                      className="admin-select admin-select--compact"
                      value={booking.issueTag || "none"}
                      onChange={(event) => handleIssueUpdate(booking, event.target.value)}
                      disabled={issueUpdatingId === booking.id}
                      aria-label={t("admin.bookings.issue", "Issue")}
                    >
                      {BOOKING_ISSUE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="admin-button admin-button--outline"
                      onClick={() => handleManualCorrection(booking.id)}
                    >
                      {t("admin.actions.adminNote", "Admin note")}
                    </button>
                  </div>
                </div>
                {expandedBookingId === booking.id && (
                  <div className="admin-booking__details">
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.phone", "Phone")}
                      </p>
                      <p className="admin-detail-value">
                        {bookingDetail.contactPhone || t("admin.bookings.detail.none", "—")}
                      </p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.item", "Item")}
                      </p>
                      <p className="admin-detail-value">{bookingDetail.item}</p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.pickup", "Pickup")}
                      </p>
                      <p className="admin-detail-value">{bookingDetail.pickup}</p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.status", "Status")}
                      </p>
                      <p className="admin-detail-value">{bookingDetail.status}</p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.issue", "Issue")}
                      </p>
                      <p className="admin-detail-value">
                        {getIssueLabel(
                          bookingDetail.issueTag ||
                            (bookingDetail.lateReturn ? "late_check_in" : "none")
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.type", "Type")}
                      </p>
                      <p className="admin-detail-value">
                        {bookingDetail.type || t("admin.bookings.detail.none", "—")}
                      </p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.totalPaid", "Total paid")}
                      </p>
                      <p className="admin-detail-value">
                        {formatCurrency(bookingDetail.totalPaid, bookingDetail.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.paymentStatus", "Payment status")}
                      </p>
                      <p className="admin-detail-value">{bookingDetail.paymentStatus}</p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.voucher", "Voucher")}
                      </p>
                      <p className="admin-detail-value">
                        {bookingDetail.rewardVoucherCode ||
                          t("admin.bookings.detail.none", "—")}
                      </p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.checkedOut", "Checked out")}
                      </p>
                      <p className="admin-detail-value">
                        {formatTimestamp(bookingDetail.checkedOutUtc)}
                      </p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.checkedIn", "Checked in")}
                      </p>
                      <p className="admin-detail-value">
                        {formatTimestamp(bookingDetail.checkedInUtc)}
                      </p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.createdVia", "Created via")}
                      </p>
                      <p className="admin-detail-value">{bookingDetail.createdVia}</p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.adminNote", "Admin note")}
                      </p>
                      <p className="admin-detail-value">
                        {bookingDetail.adminNote || t("admin.bookings.detail.none", "—")}
                      </p>
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.qrStatus", "QR status")}
                      </p>
                      <p className="admin-detail-value">{bookingTokenStatus}</p>
                      {bookingTokenExpires && (
                        <p className="admin-item__note">
                          {t("admin.bookings.detail.qrExpires", "Expires")}{" "}
                          {formatTimestamp(bookingTokenExpires)}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.qrLink", "QR link")}
                      </p>
                      {bookingTokenActionUrl ? (
                        <a
                          className="admin-detail-link"
                          href={bookingTokenActionUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("admin.bookings.detail.qrOpen", "Open QR link")}
                        </a>
                      ) : (
                        <p className="admin-detail-value">
                          {t("admin.bookings.detail.none", "—")}
                        </p>
                      )}
                    </div>
                    <div className="admin-detail-actions">
                      <p className="admin-detail-label">
                        {t("admin.bookings.detail.qrActions", "QR actions")}
                      </p>
                      <div className="admin-inline-actions admin-inline-actions--compact">
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          onClick={() => handleResendBookingToken(booking.id)}
                          disabled={bookingTokenActionId === booking.id || isDetailLoading}
                        >
                          {t("admin.bookings.qrResend", "Resend")}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button--outline"
                          onClick={() => handleReissueBookingToken(booking.id)}
                          disabled={bookingTokenActionId === booking.id || isDetailLoading}
                        >
                          {t("admin.bookings.qrRecreate", "Recreate")}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button--danger"
                          onClick={() => handleRevokeBookingToken(booking.id)}
                          disabled={bookingTokenActionId === booking.id || isDetailLoading}
                        >
                          {t("admin.bookings.qrCancel", "Cancel")}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {correctionBookingId === booking.id && (
                  <form
                    className="admin-correction"
                    onSubmit={(event) => handleCorrectionSubmit(event, booking.id)}
                  >
                    <label className="admin-form__label" htmlFor={`correction-${booking.id}`}>
                      {t("admin.bookings.noteLabel", "Admin note")}
                    </label>
                    <textarea
                      id={`correction-${booking.id}`}
                      className="admin-input admin-input--area"
                      value={correctionNote}
                      onChange={(event) => setCorrectionNote(event.target.value)}
                      placeholder={t(
                        "admin.bookings.notePlaceholder",
                        "Add an internal note for this booking"
                      )}
                      rows={3}
                    />
                    <div className="admin-inline-actions">
                      <button
                        type="submit"
                        className="admin-button admin-button--primary"
                        disabled={isSavingNote}
                      >
                        {t("admin.bookings.noteSave", "Save note")}
                      </button>
                      <button
                        type="button"
                        className="admin-button admin-button--ghost"
                        onClick={() => {
                          setCorrectionBookingId(null);
                          setCorrectionNote("");
                        }}
                      >
                        {t("admin.bookings.noteCancel", "Cancel")}
                      </button>
                    </div>
                  </form>
                )}
              </article>
            );
          })
        )}
      </div>
      {!bookingsLoading && !bookingsError && bookings.length > 0 && (
        <div className="admin-money__pagination">
          <button
            type="button"
            className="admin-button admin-button--ghost"
            onClick={() => setBookingsPage((prev) => Math.max(prev - 1, 1))}
            disabled={bookingsPage <= 1}
            aria-label={t("admin.actions.prev", "Prev")}
          >
            {"<"}
          </button>
          <span className="admin-money__page">
            {t("admin.bookings.page", "Page")} {bookingsPage}{" "}
            {t("admin.bookings.of", "of")} {totalBookingsPages}
          </span>
          <button
            type="button"
            className="admin-button admin-button--ghost"
            onClick={() =>
              setBookingsPage((prev) =>
                prev * bookingsPageSize < bookingsTotal ? prev + 1 : prev
              )
            }
            disabled={bookingsPage * bookingsPageSize >= bookingsTotal}
            aria-label={t("admin.actions.next", "Next")}
          >
            {">"}
          </button>
        </div>
      )}
    </section>
  </div>
);

export default AdminBookingsTab;
